import React from 'react';
import {
  Box,
  Icons,
  InputBase,
  TextField,
  Typography,
} from '@ublend-npm/aulaui-core';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import StudentRow from './StudentRow';
import {
  ExtensionStudent,
  ManageExtensionStyles,
} from './ManageExtension.types';

type Props = {
  extensionName: string;
  isLoading: boolean;
  onExtensionNameChange: (value: string) => void;
  onOpenCohorts: () => void;
  onSearchChange: (value: string) => void;
  onToggleStudent: (studentId: string) => void;
  search: string;
  searchResults: ExtensionStudent[];
  selectedIds: string[];
  selectedStudents: ExtensionStudent[];
  studentsCount: number;
  styles: ManageExtensionStyles;
  unselectedCount: number;
};

const ExtensionStudentsStep = ({
  extensionName,
  isLoading,
  onExtensionNameChange,
  onOpenCohorts,
  onSearchChange,
  onToggleStudent,
  search,
  searchResults,
  selectedIds,
  selectedStudents,
  studentsCount,
  styles,
  unselectedCount,
}: Props) => {
  const visibleStudents = search ? searchResults : selectedStudents;

  return (
    <Box sx={styles.studentsStep}>
      <Typography sx={styles.extensionCount}>Extension info</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <TextField
          autoFocus
          label="Extension name"
          placeholder="Extension name"
          aria-label="Extension name"
          value={extensionName}
          helperText="Students added here will be removed from cohorts"
          sx={styles.extensionNameTextField}
          onChange={(event) => onExtensionNameChange(event.target.value)}
        />
      </Box>
      <Box sx={styles.summaryHeader}>
        <Typography component="h3" sx={styles.sectionTitle}>
          Assign students ({selectedIds.length})
        </Typography>
        {!!unselectedCount && (
          <Box
            component="button"
            aria-label={`${unselectedCount} in other cohorts`}
            sx={styles.otherCohortsButton}
            onClick={onOpenCohorts}
          >
            {unselectedCount} in other cohorts{' '}
            <Icons.ArrowForward sx={{ height: '18px', width: '18px' }} />
          </Box>
        )}
      </Box>
      <Box sx={styles.chip}>Students</Box>
      <InputBase
        fullWidth
        placeholder="Search for students"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        startAdornment={<Icons.Search sx={styles.searchIcon} />}
        sx={styles.searchInput}
        inputProps={{
          'aria-label': 'Search for students',
          role: 'searchbox',
        }}
      />
      {visibleStudents && visibleStudents.length > 0 && (
        <Box component="ul" sx={styles.studentList}>
          {visibleStudents.map((student) => (
            <StudentRow
              key={student.id}
              canAdd={Boolean(search)}
              isSelected={selectedIds.includes(student.id)}
              onToggle={onToggleStudent}
              student={student}
              styles={styles}
            />
          ))}
        </Box>
      )}
      {!isLoading && visibleStudents.length === 0 && search && (
        <EmptyStudents />
      )}
    </Box>
  );
};

export default ExtensionStudentsStep;

const EmptyStudents = () => {
  return (
    <NoResultsCard
      title="No student found!"
      description="Remember to check the spelling."
    />
  );
};
